import { Box, Button, Stack, Typography, useTheme } from "@mui/material";
import { useState } from "react";

/**
 * Shown when a previous data era's IndexedDB database is still held open by another Storyboard
 * tab, so this tab's one-time migration cannot complete.
 *
 * IndexedDB offers no forced drop: the delete stays queued until every other connection closes,
 * and until then reads on those names queue behind it. The only action that resolves it is the
 * user closing the other tabs, so the screen asks for exactly that and offers a retry — unlike
 * `StorageError`, clearing local data here would not help and would cost the user their cache.
 */
export default function BlockedByOtherTabs() {
  const theme = useTheme();
  const [busy, setBusy] = useState(false);

  const handleRetry = () => {
    setBusy(true);
    window.location.reload();
  };

  return (
    <Stack
      role="alert"
      sx={{ alignItems: "center", justifyContent: "center", textAlign: "center", minHeight: "100dvh", px: 3, py: 6 }}
    >
      <Box
        sx={{
          maxWidth: 460,
          width: "100%",
          p: 4,
          borderRadius: 3,
          backgroundColor: theme.palette.brand.other.black.main,
          border: `1px solid ${theme.palette.secondary.main}`,
          boxShadow: theme.shadows[8],
        }}
      >
        <Typography variant="h5" component="h1" sx={{ mb: 2, fontWeight: 700 }}>
          Close your other Storyboard tabs
        </Typography>
        <Typography variant="body1" sx={{ mb: 4, color: theme.palette.text.secondary }}>
          Storyboard is updating the data it keeps on this device, and another open tab is still using the old copy.
          Close the other tabs, then continue.
        </Typography>
        <Button variant="contained" color="primary" onClick={handleRetry} disabled={busy} fullWidth>
          {busy ? "Retrying…" : "Continue"}
        </Button>
      </Box>
    </Stack>
  );
}
